import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import "./CarShorts.css";
import { MyContext } from "../../Context/MyContext";
import { parseYoutubeVideoId } from "../../../Utils/youtubeUtils";

/** Approved listings with a valid YouTube Shorts / embed URL */
export function getCarHighlightVideos(cars = []) {
  return cars.filter((car) => {
    const isApproved = !car.status || car.status === "approved";
    return isApproved && Boolean(parseYoutubeVideoId(car.shorts));
  });
}

export default function CarShorts() {
  const { cars } = useContext(MyContext);
  const shortsCars = useMemo(() => getCarHighlightVideos(cars), [cars]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const playersRef = useRef([]);
  const rowRef = useRef(null);

  const initPlayers = useCallback(() => {
    if (!window.YT?.Player || !shortsCars.length) return;

    playersRef.current.forEach((player) => {
      try {
        player?.destroy?.();
      } catch {
        /* player may already be destroyed */
      }
    });
    playersRef.current = [];

    shortsCars.forEach((car, index) => {
      const videoId = parseYoutubeVideoId(car.shorts);
      if (!videoId) return;

      const player = new window.YT.Player(`player-${car.id}`, {
        videoId,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
        },
        events: {
          onStateChange: (event) => {
            if (event.data === 1) {
              playersRef.current.forEach((p) => {
                if (p !== event.target && p?.pauseVideo) {
                  p.pauseVideo();
                }
              });
            }
          },
        },
      });

      playersRef.current.push(player);
    });
  }, [shortsCars]);

  useEffect(() => {
    if (!shortsCars.length) return undefined;

    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);

      window.onYouTubeIframeAPIReady = () => {
        initPlayers();
      };
    } else if (window.YT?.Player) {
      initPlayers();
    }

    return () => {
      playersRef.current.forEach((player) => {
        try {
          player?.destroy?.();
        } catch {
          /* ignore */
        }
      });
      playersRef.current = [];
    };
  }, [initPlayers, shortsCars]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [shortsCars.length]);

  const pauseAllVideos = () => {
    playersRef.current.forEach((player) => {
      if (player?.pauseVideo) {
        player.pauseVideo();
      }
    });
  };

  const scrollToIndex = (index) => {
    const row = rowRef.current;
    const card = row?.querySelector(".short-card");
    if (!row || !card) return;

    const gap = 20;
    const scrollAmount = index * (card.offsetWidth + gap);

    row.scrollTo({
      left: scrollAmount,
      behavior: "smooth",
    });
  };

  const next = () => {
    if (currentIndex < shortsCars.length - 1) {
      pauseAllVideos();
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      scrollToIndex(nextIndex);
    }
  };

  const prev = () => {
    if (currentIndex > 0) {
      pauseAllVideos();
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
      scrollToIndex(prevIndex);
    }
  };

  if (!shortsCars.length) return null;

  return (
    <section className="shorts-section">
      <div className="shorts-header">
        <div>
          <h2 className="shorts-title">Car Highlights</h2>
          <p className="shorts-subtitle">
            Short videos from approved listings with a YouTube Shorts link
          </p>
        </div>

        <div className="shorts-arrows">
          <button
            type="button"
            className="shorts-arrow-btn"
            onClick={prev}
            disabled={currentIndex === 0}
            aria-label="Previous video"
          >
            ‹
          </button>
          <button
            type="button"
            className="shorts-arrow-btn"
            onClick={next}
            disabled={currentIndex === shortsCars.length - 1}
            aria-label="Next video"
          >
            ›
          </button>
        </div>
      </div>

      <div className="shorts-row" ref={rowRef}>
        {shortsCars.map((car) => (
          <article key={car.id} className="short-card">
            <div className="short-card__player" id={`player-${car.id}`} />

            <div className="short-info">
              <div className="short-price">₹{car.price?.toLocaleString()}</div>
              <div className="short-meta">
                {car.year} • {car.km_driven?.toLocaleString()} km
              </div>
            </div>

            <Link to={`/car/${car.id}`} className="short-card__link">
              View car details
            </Link>
          </article>
        ))}
      </div>
    </section>
  );
}
